import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, CheckCircle, XCircle, Search, RefreshCw, 
  Trash2, ChevronDown, ChevronUp, Play, Filter, Download,
  BarChart3, AlertOctagon, ThumbsUp, BookOpen, FileQuestion,
  ArrowRight, Eye, Edit, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentAudit = ({ professions, onNavigateToReview }) => {
  const [loading, setLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState('');
  const [contentStats, setContentStats] = useState(null);
  const [lowQualityQuestions, setLowQualityQuestions] = useState([]);
  const [lowQualityFlashcards, setLowQualityFlashcards] = useState([]);
  const [topicAudit, setTopicAudit] = useState([]);
  const [flashcardTopicAudit, setFlashcardTopicAudit] = useState([]);
  const [auditReport, setAuditReport] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectedFlashcards, setSelectedFlashcards] = useState([]);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [activeTab, setActiveTab] = useState('questions'); // 'questions' | 'flashcards'

  useEffect(() => {
    loadContentStats();
  }, []);

  useEffect(() => {
    if (selectedProfession) {
      if (activeTab === 'questions') {
        loadLowQualityQuestions();
        loadTopicAudit();
      } else {
        loadLowQualityFlashcards();
        loadFlashcardTopicAudit();
      }
    }
  }, [selectedProfession, activeTab]);

  const loadContentStats = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/audit/questions/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContentStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadLowQualityQuestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/audit/questions/low-quality?profession_id=${selectedProfession}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLowQualityQuestions(res.data.questions || []);
    } catch (error) {
      toast.error('Error al cargar preguntas de baja calidad');
    } finally {
      setLoading(false);
    }
  };

  const loadLowQualityFlashcards = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/audit/flashcards/low-quality?profession_id=${selectedProfession}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLowQualityFlashcards(res.data.flashcards || []);
    } catch (error) {
      // If endpoint doesn't exist, show empty
      setLowQualityFlashcards([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTopicAudit = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/audit/questions/by-topic?profession_id=${selectedProfession}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopicAudit(res.data.topics || []);
    } catch (error) {
      console.error('Error loading topic audit:', error);
    }
  };

  const loadFlashcardTopicAudit = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/audit/flashcards/by-topic?profession_id=${selectedProfession}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlashcardTopicAudit(res.data.topics || []);
    } catch (error) {
      // Create from stats if endpoint doesn't exist
      setFlashcardTopicAudit([]);
    }
  };

  const runFullAudit = async () => {
    if (!selectedProfession) {
      toast.error('Selecciona una profesión primero');
      return;
    }

    setAuditLoading(true);
    toast.info('Ejecutando auditoría completa... Esto puede tardar unos minutos.');
    
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/audit/run-full-audit?profession_id=${selectedProfession}&include_flashcards=true`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000
      });
      setAuditReport(res.data);
      toast.success('Auditoría completada');
    } catch (error) {
      toast.error('Error al ejecutar la auditoría');
    } finally {
      setAuditLoading(false);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta pregunta?')) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/audit/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pregunta eliminada');
      setLowQualityQuestions(prev => prev.filter(q => q.question_id !== questionId));
    } catch (error) {
      toast.error('Error al eliminar la pregunta');
    }
  };

  const deleteFlashcard = async (flashcardId) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta flashcard?')) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/admin/content/flashcards/${flashcardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flashcard eliminada');
      setLowQualityFlashcards(prev => prev.filter(f => f.flashcard_id !== flashcardId));
    } catch (error) {
      toast.error('Error al eliminar la flashcard');
    }
  };

  const deleteSelectedQuestions = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('Selecciona preguntas para eliminar');
      return;
    }

    if (!window.confirm(`¿Eliminar ${selectedQuestions.length} preguntas?`)) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/audit/questions/batch`, {
        headers: { Authorization: `Bearer ${token}` },
        data: selectedQuestions
      });
      toast.success(`${selectedQuestions.length} preguntas eliminadas`);
      setLowQualityQuestions(prev => prev.filter(q => !selectedQuestions.includes(q.question_id)));
      setSelectedQuestions([]);
    } catch (error) {
      toast.error('Error al eliminar las preguntas');
    }
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const toggleFlashcardSelection = (flashcardId) => {
    setSelectedFlashcards(prev => 
      prev.includes(flashcardId) 
        ? prev.filter(id => id !== flashcardId)
        : [...prev, flashcardId]
    );
  };

  const selectAllLowQuality = () => {
    if (activeTab === 'questions') {
      if (selectedQuestions.length === lowQualityQuestions.length) {
        setSelectedQuestions([]);
      } else {
        setSelectedQuestions(lowQualityQuestions.map(q => q.question_id));
      }
    } else {
      if (selectedFlashcards.length === lowQualityFlashcards.length) {
        setSelectedFlashcards([]);
      } else {
        setSelectedFlashcards(lowQualityFlashcards.map(f => f.flashcard_id));
      }
    }
  };

  const getQualityColor = (score) => {
    if (score >= 90) return 'text-emerald-600 bg-emerald-100';
    if (score >= 70) return 'text-blue-600 bg-blue-100';
    if (score >= 50) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  // Navigate to review section for a specific topic
  const handleNavigateToReview = (topicId, type = 'questions') => {
    if (onNavigateToReview) {
      onNavigateToReview(selectedProfession, topicId, type);
    } else {
      toast.info(`Navega a Gestión de Contenido > Revisar ${type === 'questions' ? 'Preguntas' : 'Flashcards'} y selecciona el tema`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Auditoría de Calidad</h2>
          <p className="text-slate-600">Analiza y gestiona la calidad del contenido</p>
        </div>
        <Button variant="outline" onClick={loadContentStats} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Overview */}
      {contentStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
              <FileQuestion className="w-4 h-4" />
              Total Preguntas
            </div>
            <p className="text-2xl font-bold text-slate-900">{contentStats.total_questions?.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
              <BookOpen className="w-4 h-4" />
              Total Flashcards
            </div>
            <p className="text-2xl font-bold text-slate-900">{contentStats.total_flashcards?.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
              <BarChart3 className="w-4 h-4" />
              Total Opciones
            </div>
            <p className="text-2xl font-bold text-slate-900">{contentStats.total_options?.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
              <BarChart3 className="w-4 h-4" />
              Profesiones
            </div>
            <p className="text-2xl font-bold text-slate-900">{contentStats.by_profession?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Profession Selector */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="font-semibold text-slate-900 mb-4">Selecciona una profesión para auditar</h3>
        <div className="flex gap-4 items-center">
          <select
            value={selectedProfession}
            onChange={(e) => setSelectedProfession(e.target.value)}
            className="flex-1 border border-slate-200 rounded-lg px-4 py-2"
          >
            <option value="">-- Seleccionar profesión --</option>
            {professions.map(prof => (
              <option key={prof.profession_id} value={prof.profession_id}>
                {prof.name} ({contentStats?.by_profession?.find(p => p.profession_id === prof.profession_id)?.questions || 0} preguntas)
              </option>
            ))}
          </select>
          <Button
            onClick={runFullAudit}
            disabled={!selectedProfession || auditLoading}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {auditLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Auditando...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Ejecutar Auditoría Completa
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Audit Report */}
      {auditReport && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Informe de Auditoría
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Total</p>
              <p className="text-2xl font-bold text-slate-900">{auditReport.total_questions}</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-600">Excelente</p>
              <p className="text-2xl font-bold text-emerald-700">{auditReport.summary?.excellent || 0}</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">Bueno</p>
              <p className="text-2xl font-bold text-blue-700">{auditReport.summary?.good || 0}</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-600">Revisar</p>
              <p className="text-2xl font-bold text-amber-700">{auditReport.summary?.needs_review || 0}</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600">Pobre</p>
              <p className="text-2xl font-bold text-red-700">{auditReport.summary?.poor || 0}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 via-blue-500 to-red-500"
                style={{ width: `${auditReport.overall_quality_score || 0}%` }}
              />
            </div>
            <span className="font-bold text-lg">{auditReport.overall_quality_score || 0}%</span>
          </div>
        </div>
      )}

      {/* Content Type Tabs */}
      {selectedProfession && (
        <div className="flex gap-2 border-b pb-2">
          <Button
            variant={activeTab === 'questions' ? 'default' : 'outline'}
            onClick={() => setActiveTab('questions')}
            className="flex items-center gap-2"
          >
            <FileQuestion className="w-4 h-4" />
            Preguntas
          </Button>
          <Button
            variant={activeTab === 'flashcards' ? 'default' : 'outline'}
            onClick={() => setActiveTab('flashcards')}
            className="flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Flashcards
          </Button>
        </div>
      )}

      {/* Topic Audit Summary - Questions */}
      {selectedProfession && activeTab === 'questions' && topicAudit.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Calidad por Tema - Preguntas</h3>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Tema</th>
                  <th className="text-center p-3 font-medium text-slate-600">Total</th>
                  <th className="text-center p-3 font-medium text-slate-600">Buenos</th>
                  <th className="text-center p-3 font-medium text-slate-600">Revisar</th>
                  <th className="text-center p-3 font-medium text-slate-600">Calidad</th>
                  <th className="text-center p-3 font-medium text-slate-600">Acción</th>
                </tr>
              </thead>
              <tbody>
                {topicAudit.map(topic => (
                  <tr key={topic.topic_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 text-slate-900">{topic.topic_name}</td>
                    <td className="p-3 text-center text-slate-600">{topic.total_questions}</td>
                    <td className="p-3 text-center text-emerald-600">{topic.good_quality}</td>
                    <td className="p-3 text-center">
                      {topic.needs_review > 0 ? (
                        <span className="text-amber-600 font-medium">{topic.needs_review}</span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getQualityColor(topic.quality_percentage)}`}>
                        {topic.quality_percentage}%
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {topic.needs_review > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleNavigateToReview(topic.topic_id, 'questions')}
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver {topic.needs_review}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Topic Audit Summary - Flashcards */}
      {selectedProfession && activeTab === 'flashcards' && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Calidad por Tema - Flashcards</h3>
          {flashcardTopicAudit.length > 0 ? (
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600">Tema</th>
                    <th className="text-center p-3 font-medium text-slate-600">Total</th>
                    <th className="text-center p-3 font-medium text-slate-600">Buenos</th>
                    <th className="text-center p-3 font-medium text-slate-600">Revisar</th>
                    <th className="text-center p-3 font-medium text-slate-600">Calidad</th>
                    <th className="text-center p-3 font-medium text-slate-600">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {flashcardTopicAudit.map(topic => (
                    <tr key={topic.topic_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-slate-900">{topic.topic_name}</td>
                      <td className="p-3 text-center text-slate-600">{topic.total_flashcards}</td>
                      <td className="p-3 text-center text-emerald-600">{topic.good_quality}</td>
                      <td className="p-3 text-center">
                        {topic.needs_review > 0 ? (
                          <span className="text-amber-600 font-medium">{topic.needs_review}</span>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getQualityColor(topic.quality_percentage)}`}>
                          {topic.quality_percentage}%
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        {topic.needs_review > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleNavigateToReview(topic.topic_id, 'flashcards')}
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver {topic.needs_review}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay datos de auditoría de flashcards disponibles.</p>
              <p className="text-sm mt-2">Ejecuta una auditoría completa para obtener datos.</p>
            </div>
          )}
        </div>
      )}

      {/* Low Quality Content - Questions */}
      {selectedProfession && activeTab === 'questions' && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Preguntas de Baja Calidad ({lowQualityQuestions.length})
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllLowQuality}>
                {selectedQuestions.length === lowQualityQuestions.length ? 'Deseleccionar' : 'Seleccionar todo'}
              </Button>
              {selectedQuestions.length > 0 && (
                <Button variant="destructive" size="sm" onClick={deleteSelectedQuestions}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar ({selectedQuestions.length})
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
              <p className="text-slate-500">Analizando...</p>
            </div>
          ) : lowQualityQuestions.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              <p className="text-slate-600">¡No se encontraron preguntas de baja calidad!</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {lowQualityQuestions.map(q => (
                <div 
                  key={q.question_id}
                  className={`border rounded-lg p-4 transition-all ${
                    selectedQuestions.includes(q.question_id) 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.includes(q.question_id)}
                      onChange={() => toggleQuestionSelection(q.question_id)}
                      className="mt-1 w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getQualityColor(q.quality_score)}`}>
                          Score: {q.quality_score}
                        </span>
                        {q.topic_name && (
                          <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                            {q.topic_name}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-900 mb-2">{q.question_text}</p>
                      <div className="flex flex-wrap gap-1">
                        {(q.issues || []).map((issue, idx) => (
                          <span key={idx} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            {issue}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteQuestion(q.question_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Low Quality Content - Flashcards */}
      {selectedProfession && activeTab === 'flashcards' && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Flashcards de Baja Calidad ({lowQualityFlashcards.length})
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllLowQuality}>
                {selectedFlashcards.length === lowQualityFlashcards.length ? 'Deseleccionar' : 'Seleccionar todo'}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
              <p className="text-slate-500">Analizando flashcards...</p>
            </div>
          ) : lowQualityFlashcards.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              <p className="text-slate-600">¡No se encontraron flashcards de baja calidad!</p>
              <p className="text-sm text-slate-400 mt-2">
                Nota: La auditoría de flashcards analiza contenido muy corto, duplicados y errores de formato.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {lowQualityFlashcards.map(fc => (
                <div 
                  key={fc.flashcard_id}
                  className={`border rounded-lg p-4 transition-all ${
                    selectedFlashcards.includes(fc.flashcard_id) 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedFlashcards.includes(fc.flashcard_id)}
                      onChange={() => toggleFlashcardSelection(fc.flashcard_id)}
                      className="mt-1 w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getQualityColor(fc.quality_score || 50)}`}>
                          Score: {fc.quality_score || 50}
                        </span>
                        {fc.topic_name && (
                          <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                            {fc.topic_name}
                          </span>
                        )}
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-blue-600 mb-1">CARA A</p>
                          <p className="text-sm text-slate-900">{fc.front}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-green-600 mb-1">CARA B</p>
                          <p className="text-sm text-slate-900">{fc.back}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(fc.issues || []).map((issue, idx) => (
                          <span key={idx} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            {issue}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteFlashcard(fc.flashcard_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentAudit;
